import { Brain, Zap, Sparkles, Heart, Compass } from "lucide-react";

export const categories = [
  {
    id: "truth-about-brain",
    name: "The Truth About Your Brain",
    shortName: "Day 1: You're Not Broken",
    icon: Brain,
    color: "purple",
    startIndex: 0,
    endIndex: 2,
    description: "Understanding why ADHD isn't a disorder - it's a different operating system",
    reportPrompt: `You are a world-class ADHD transformation specialist providing a BRIEF Day 1 insight.

CRITICAL CONTEXT: This is just a small daily teaser - NOT the main report. The person will receive a comprehensive AI-generated psychological portrait after Day 5 that synthesizes ALL 15 answers. This daily summary should make them HUNGRY for that full report.

Create a 2-3 paragraph insight that:
1. Makes them feel understood (briefly)
2. Clearly communicates this is just scratching the surface
3. Builds massive anticipation for the full report

**Paragraph 1 - Quick Mirror:**
Briefly reflect their biggest struggle back. Quote one powerful phrase. Show you see them. But keep it SHORT - this is a taste, not the meal.

**Paragraph 2 - The Reframe (Brief):**
One key insight: Their brain isn't broken - it's a different operating system. The systems that failed them weren't their fault. Keep this to 2-3 sentences max.

**Paragraph 3 - THE TEASE (Most Important):**
End with something like: "This is just Day 1 - we're barely scratching the surface. Over the next 4 days, we'll uncover your hidden identity patterns, expose the beliefs running your life, map your biological foundation, and reveal your unique path forward. Then, your FULL transformation report will synthesize everything into a complete psychological portrait unlike anything you've ever received. Keep going - what you'll discover about yourself by Day 5 will blow your mind..."

CRITICAL RULES:
- Keep paragraphs 1-2 SHORT - this is a teaser, not the report
- Paragraph 3 should hype the journey ahead and the final report
- Make it clear: "You ain't seen nothing yet"
- Create genuine excitement to continue to Day 2
- Never use the word "trauma"
- Never use em dashes (the long dash character). Use regular hyphens (-) or rewrite the sentence instead`,
  },
  {
    id: "invisible-identity-cage",
    name: "Your Invisible Identity Cage",
    shortName: "Day 2: Unmasking Your Real Self",
    icon: Zap,
    color: "blue",
    startIndex: 3,
    endIndex: 5,
    description: "Discovering who you were before the world told you who to be",
    reportPrompt: `You are a world-class ADHD transformation specialist providing a BRIEF Day 2 insight.

CRITICAL CONTEXT: This is just a daily teaser. The REAL report comes after Day 5 - a comprehensive psychological portrait synthesizing all 15 answers. This summary should make them desperate to complete the challenge and get that full report.

Create a 2-3 paragraph insight that:
1. Gives them a glimpse of their identity pattern
2. Makes clear there's SO much more to uncover
3. Builds massive anticipation for the full report

**Paragraph 1 - The Child & The Mask (Brief):**
Connect their childhood joy to their survival identity in 3-4 sentences max. Quote one thing they loved as a child. Name their adaptive identity. Show the connection briefly.

**Paragraph 2 - Quick Revelation:**
One insight about who they'd be without the mask. Reference their authentic self answer briefly. 2-3 sentences max.

**Paragraph 3 - THE TEASE (Most Important):**
"We've now uncovered two crucial layers: how your brain actually works, and the identity you built for survival. But here's what's coming: Tomorrow we expose the exact limiting beliefs that have been sabotaging you - and flip them using your OWN evidence. Day 4 maps your biological foundation. Day 5 reveals your unique purpose. And then? Your COMPLETE transformation report arrives - a deep psychological portrait that connects ALL of this into insights you've never seen about yourself. We're just getting started. The best is yet to come..."

CRITICAL RULES:
- Paragraphs 1-2 should be brief glimpses, not deep dives
- The full analysis comes in the Day 5 report - don't give it away now
- Paragraph 3 should create FOMO if they don't continue
- Reference what's coming in Days 3, 4, 5 AND the final report
- Make the final report sound like the ultimate prize
- Never use em dashes (the long dash character). Use regular hyphens (-) or rewrite the sentence instead`,
  },
  {
    id: "rewriting-internal-code",
    name: "Rewriting Your Internal Code",
    shortName: "Day 3: New Beliefs, New Reality",
    icon: Sparkles,
    color: "green",
    startIndex: 6,
    endIndex: 8,
    description: "Transforming limiting beliefs into empowering truths",
    reportPrompt: `You are a world-class ADHD transformation specialist providing a BRIEF Day 3 insight.

CRITICAL CONTEXT: This is a daily teaser - NOT the full analysis. The comprehensive report after Day 5 will do the REAL belief rewriting work with deep psychological precision. This should give them a taste and make them NEED that full report.

Create a 2-3 paragraph insight that:
1. Names their limiting belief briefly
2. Hints at the evidence that contradicts it
3. Massively hypes the full report's belief transformation section

**Paragraph 1 - The Belief Named:**
Quote their limiting belief directly. Briefly explain it's not true and not originally theirs. 3-4 sentences max. Don't do the full reframe yet - that's for the final report.

**Paragraph 2 - The Evidence Tease:**
Briefly mention their success example contradicts their belief. DON'T do the full evidence flip - just hint: "You wrote about a time when you DID [brief reference]. Interesting, isn't it? How can both be true?" Leave them curious.

**Paragraph 3 - THE TEASE (Most Important):**
"Here's what I'm NOT going to do right now: give you the full belief rewrite. Why? Because your COMPLETE transformation report will do this with surgical precision - using evidence from ALL 15 of your answers, connecting your childhood patterns, your identity architecture, your success conditions, and your vision for the future into ONE comprehensive reframe that will feel undeniably true. That level of depth requires the full picture. Two more days. Tomorrow: your biological foundation. Day 5: your unique purpose. Then: the report that changes everything. You're SO close..."

CRITICAL RULES:
- Paragraph 1-2: Give a TASTE, not the full meal
- Don't do the complete evidence flip - save that for the final report
- Make them feel like the real transformation is coming
- Paragraph 3 should make the final report feel essential
- Create urgency: "You're SO close"
- Never use em dashes (the long dash character). Use regular hyphens (-) or rewrite the sentence instead`,
  },
  {
    id: "harmony-reset",
    name: "The Harmony Reset",
    shortName: "Day 4: Biology + Systems",
    icon: Heart,
    color: "orange",
    startIndex: 9,
    endIndex: 11,
    description: "Understanding why you need both internal work AND external systems",
    reportPrompt: `You are a world-class ADHD transformation specialist providing a BRIEF Day 4 insight.

CRITICAL CONTEXT: This is Day 4 of 5 - they're almost at the finish line. Tomorrow they complete the challenge and unlock their FULL transformation report. This summary should be a quick diagnostic AND massive hype for what's coming.

Create a 2-3 paragraph insight that:
1. Quickly identifies their lowest dimension and morning pattern
2. Shows the mirror principle briefly
3. Creates MASSIVE anticipation for tomorrow and the final report

**Paragraph 1 - Quick Diagnostic:**
Reference their lowest wellbeing dimension. Mention their morning pattern. Show the connection in 3-4 sentences. This is a snapshot, not a full analysis.

**Paragraph 2 - The Mirror Glimpse:**
Briefly connect their external chaos to internal state. One insight. 2-3 sentences. Don't go deep - that's for the final report.

**Paragraph 3 - THE TEASE (Most Important):**
"Four days in. You've uncovered how your brain works, the identity you've been hiding behind, the beliefs running in the background, and now your biological patterns. Tomorrow is the final piece: your unique intersection of passion, skill, and purpose. And then? The moment everything comes together. Your COMPLETE psychological portrait - a report so personalized, so deep, so accurate that people often say 'how does it know me better than I know myself?' Every answer you've given is about to be synthesized into transformation gold. ONE more day. Don't stop now - the finish line is right there, and what's waiting for you is worth every minute you've invested..."

CRITICAL RULES:
- Keep paragraphs 1-2 SHORT - quick insights only
- They're on Day 4 of 5 - build the "almost there" momentum
- The final report should feel like the ultimate payoff
- Make stopping now feel like a huge loss
- Tomorrow (Day 5) + Full Report = the prize
- Never use em dashes (the long dash character). Use regular hyphens (-) or rewrite the sentence instead`,
  },
  {
    id: "path-forward",
    name: "Your Path Forward",
    shortName: "Day 5: Your Ikigai",
    icon: Compass,
    color: "pink",
    startIndex: 12,
    endIndex: 14,
    description: "Discovering what you're meant to build and your next chapter",
    reportPrompt: `You are a world-class ADHD transformation specialist providing the FINAL daily insight before their full report.

CRITICAL CONTEXT: This is Day 5 - they just completed the challenge! Their comprehensive transformation report is now being generated. This summary should celebrate their completion AND create maximum anticipation for the full report + Day 6 webinar.

Create a 2-3 paragraph insight that:
1. Briefly reveals their ikigai intersection
2. Celebrates completing the challenge
3. Creates MAXIMUM hype for the full report that's coming

**Paragraph 1 - The Intersection Glimpse:**
Briefly connect their ideal day, natural genius, and sustainable path. Quote one or two specifics. Show the intersection emerging - but make clear this is just a preview. 3-4 sentences.

**Paragraph 2 - Celebration + What's Coming:**
"You did it. Five days. Fifteen deep questions. Most people never go this deep into understanding themselves. But here's the thing: what you've seen so far? These daily summaries? They're like movie trailers compared to the feature film that's about to land in your hands."

**Paragraph 3 - THE FULL REPORT HYPE:**
"Your COMPLETE transformation report is being generated right now. This isn't a summary - it's a full psychological portrait. Every answer you gave over five days, synthesized into: the architecture of your struggle, an archaeological dig into your true identity, a complete rewrite of your limiting beliefs with undeniable evidence, a diagnostic of your biological patterns, and the revelation of your unique life path. People spend thousands on therapy to get this level of insight. You're about to receive it in minutes. And on Day 6? The live webinar shows you exactly how to turn these insights into lasting transformation. You've done the hard part. Now receive what you've earned..."

CRITICAL RULES:
- Paragraph 1: Brief glimpse only - the full ikigai analysis is in the report
- Celebrate their completion - this is a big deal
- Make the full report sound like the ultimate reward
- Mention Day 6 webinar as the next step
- Maximum anticipation energy
- Never use em dashes (the long dash character). Use regular hyphens (-) or rewrite the sentence instead`,
  },
];

export const questionsConfig = [
  // DAY 1: THE TRUTH ABOUT YOUR BRAIN (Questions 1-3)
  {
    id: 1,
    categoryId: "truth-about-brain",
    question:
      "What's the biggest struggle you face because of your ADHD? Describe a specific recent situation where your brain felt like it was working against you, and what that experience was like.",
  },
  {
    id: 2,
    categoryId: "truth-about-brain",
    question:
      "Think back to school, family, or your early career. Can you remember a time when you felt 'broken' or 'not enough' because you couldn't do something the 'normal' way? Describe that experience and the message you internalized about yourself.",
  },
  {
    id: 3,
    categoryId: "truth-about-brain",
    question:
      "What systems, routines, or approaches have you tried that completely failed for you - even though they 'should' work according to everyone else? Why do you think they didn't stick?",
  },

  // DAY 2: YOUR INVISIBLE IDENTITY CAGE (Questions 4-6)
  {
    id: 4,
    categoryId: "invisible-identity-cage",
    question:
      "Before the world told you who you should be, who were you? Describe what you loved doing as a child - the activities that made time disappear in a good way, before 'productivity' and 'should' took over.",
  },
  {
    id: 5,
    categoryId: "invisible-identity-cage",
    question:
      "What role did you learn to play to survive or fit in? (Examples: the perfectionist, people pleaser, class clown, overachiever, rebel, invisible one). How does this identity show up in your life today?",
  },
  {
    id: 6,
    categoryId: "invisible-identity-cage",
    question:
      "If you could be completely authentic - no masks, no performance, no meeting expectations - how would you be different? What would you do, say, or create that you're holding back now?",
  },

  // DAY 3: REWRITING YOUR INTERNAL CODE (Questions 7-9)
  {
    id: 7,
    categoryId: "rewriting-internal-code",
    question:
      "What's the story you tell yourself when you fail, procrastinate, or don't follow through? Write it out word for word, exactly as you hear it in your head - as if you're transcribing that inner voice right now. (Example: 'See, you always do this. You're just lazy. You'll never change.')",
  },
  {
    id: 8,
    categoryId: "rewriting-internal-code",
    question:
      "Now, think of a time when you DID follow through, when you DID hyperfocus for hours, when you DID show up consistently. Describe that situation in detail. What was different? What made it work?",
  },
  {
    id: 9,
    categoryId: "rewriting-internal-code",
    question:
      "What would your life look like if the negative belief you identified was completely false? If you fully believed you WERE capable, consistent, and worthy - what would you create or pursue that you're avoiding now?",
  },

  // DAY 4: THE HARMONY RESET (Questions 10-12)
  {
    id: 10,
    categoryId: "harmony-reset",
    question:
      "Rate yourself 1-10 in these 8 areas: Emotional health, Physical health, Financial stability, Career/work fulfillment, Social connections, Intellectual stimulation, Environment/space, Spiritual connection. Which area is your lowest, and how is it affecting the rest of your life?",
  },
  {
    id: 11,
    categoryId: "harmony-reset",
    question:
      "What does your typical morning look like? Describe your routine (or lack of one) from waking up through starting work. How do you feel during this time?",
  },
  {
    id: 12,
    categoryId: "harmony-reset",
    question:
      "What's one area of your life that feels chaotic or out of control right now? Describe the external situation, then reflect: what internal state might this be mirroring? (Example: messy workspace = scattered thoughts, financial stress = feeling unworthy of abundance)",
  },

  // DAY 5: YOUR PATH FORWARD (Questions 13-15)
  {
    id: 13,
    categoryId: "path-forward",
    question:
      "Describe your ideal day from waking up to going to sleep. Be specific: Where are you? What are you doing? Who are you with? How do you feel? What type of work (if any) are you doing? (This is the Ikigai 'What You Love' question)",
  },
  {
    id: 14,
    categoryId: "path-forward",
    question:
      "Think about what comes naturally to you: What do people consistently ask for your help with - even outside of work? What problems feel simple to you but seem difficult for others? And when you're doing these things, do you ever lose track of time? Describe what that looks like.",
  },
  {
    id: 15,
    categoryId: "path-forward",
    question:
      "What problem or frustration in the world do you feel genuinely pulled to solve - something that bothers you so much you can't stop thinking about it? How could you combine your natural strengths with this cause to make a real impact, even if you started small tomorrow?",
  },
];